import { fetchClient } from "../lib/openapi-query";
import type { ChatWidget, CoachResponse } from "../types/coach";
import { validateWidgets } from "../types/widget-schemas";
import { AuthService } from "./auth";

export class CoachService {
  private static async makeStreamingRequest(
    url: string,
    body: any,
    retryCount = 0
  ): Promise<Response> {
    const token = AuthService.getStoredToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(body),
    });

    // Handle 401 Unauthorized - token might be expired
    if (response.status === 401 && retryCount === 0) {
      const refreshResult = await AuthService.refreshAccessToken();
      if (refreshResult) {
        // Retry with new token
        return this.makeStreamingRequest(url, body, retryCount + 1);
      }
      // Refresh failed, redirect to login
      AuthService.clearAuth();
      window.location.href = "/login";
      throw new Error("Session expired. Please log in again.");
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  }

  static async sendMessage(
    message: string,
    userId: string,
    threadId: string,
    onUpdate?: (update: { thinking?: string; widgets: ChatWidget[] }) => void
  ): Promise<CoachResponse> {
    try {
      const response = await this.makeStreamingRequest(
        "/api/coach/chat/send-message",
        { message, userId, threadId }
      );

      // Handle SSE response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let latestWidgets: ChatWidget[] = [];
      let finalResponse: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data !== "[DONE]") {
              try {
                const parsed = JSON.parse(data);
                console.log("SSE Event:", parsed);

                // Extract current widgets and thinking
                const rawWidgets = parsed.inChatWidgets ?? [];
                const { thinking } = parsed;

                // Validate widgets before using them
                const currentWidgets = validateWidgets(rawWidgets);

                // Update latest widgets if we have any
                if (currentWidgets.length > 0) {
                  console.log("Received valid widgets:", currentWidgets);
                  latestWidgets = currentWidgets;
                }

                // Call onUpdate callback for real-time updates
                if (onUpdate) {
                  onUpdate({
                    thinking,
                    widgets: currentWidgets,
                  });
                }

                // Handle completed response
                if (parsed.event === "completed-response") {
                  finalResponse = JSON.parse(parsed.data);
                }

                // Handle partial response events
                if (parsed.event === "partial-response") {
                  const partialData = JSON.parse(parsed.data);
                  if (
                    partialData.inChatWidgets &&
                    partialData.inChatWidgets.length > 0
                  ) {
                    const validPartialWidgets = validateWidgets(
                      partialData.inChatWidgets
                    );
                    if (validPartialWidgets.length > 0) {
                      latestWidgets = validPartialWidgets;
                    }
                  }
                }
              } catch (e) {
                console.error("Parse error:", e);
              }
            }
          }
        }
      }

      // Extract widgets from final response or use latest streamed widgets
      let widgets: ChatWidget[] = [];

      if (finalResponse) {
        widgets = this.extractWidgetsFromResponse(finalResponse);
      } else if (latestWidgets.length > 0) {
        widgets = latestWidgets;
      } else {
        widgets = [{ type: "text", text: "No response received" }];
      }

      console.log("Final widgets to return:", widgets);
      return { widgets };
    } catch (_error) {
      throw new Error("Failed to send message to coach. Please try again.");
    }
  }

  private static extractWidgetsFromResponse(response: any): ChatWidget[] {
    if (!response) {
      return [{ type: "text", text: "No response received" }];
    }

    console.log("Extracting widgets from response:", response);

    // Try to extract widgets from different possible paths
    let rawWidgets: any[] = [];

    // Check if response has widgets directly in response field
    if (response.response?.widgets) {
      rawWidgets = response.response.widgets;
    }
    // Check if response has widgets in response.inChatWidgets
    else if (response.response?.inChatWidgets) {
      rawWidgets = response.response.inChatWidgets;
    }
    // Check if response itself has widgets
    else if (response.widgets) {
      rawWidgets = response.widgets;
    }
    // Check if response has inChatWidgets
    else if (response.inChatWidgets) {
      rawWidgets = response.inChatWidgets;
    }

    // Validate and filter widgets
    const validWidgets = validateWidgets(rawWidgets);

    // If we have valid widgets, return them
    if (validWidgets.length > 0) {
      console.log("Extracted valid widgets:", validWidgets);
      return validWidgets;
    }

    // Fallback to text fields if no valid widgets
    if (response.oneSentenceResponse) {
      return [{ type: "text", text: response.oneSentenceResponse }];
    }
    if (response.responsePlan) {
      return [{ type: "text", text: response.responsePlan }];
    }
    if (typeof response === "string") {
      return [{ type: "text", text: response }];
    }

    return [
      { type: "text", text: "Received response but could not parse widgets" },
    ];
  }

  static async getChatHistory(threadId: string): Promise<any[]> {
    try {
      const response = (await fetchClient.GET("/coach/chat/history", {
        params: {
          query: {
            thread_id: threadId,
          },
        },
      })) as any;

      if (response.error) {
        console.error("Failed to fetch chat history:", response.error);
        return [];
      }

      console.log("Chat history response:", response.data);
      return response.data ?? [];
    } catch (error) {
      console.error("Failed to fetch chat history:", error);
      return [];
    }
  }

  static async clearChatHistory(): Promise<void> {
    // No clear endpoint available, just return success
    return Promise.resolve();
  }

  static async submitFeedback(
    messageId: string,
    feedback: string,
    userId: string,
    sessionId?: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = (await fetchClient.POST("/feedback", {
        body: {
          messageId,
          feedback,
          userId,
          sessionId,
        },
      })) as any;

      if (response.error) {
        console.error("Failed to submit feedback:", response.error);
        return {
          success: false,
          error:
            response.error.message ??
            "Failed to submit feedback. Please try again.",
        };
      }

      return (response.data ?? { success: true }) as {
        success: boolean;
        message?: string;
        error?: string;
      };
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      return {
        success: false,
        error: "Failed to submit feedback. Please try again.",
      };
    }
  }
}
